import FetchClients, { ClientOutput } from "../client";
import {
  useMutation,
  useQuery as useReactQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { CamelCasedProperties } from "type-fest";
import {
  recursiveCamelCaseCipherTypesafe,
  recursiveSnakeCaseCipherTypesafe,
} from "@src/global_functions/postgrestApi";

const linking = {
  useGetLinkedEnergyStarProperties,
  mutations: {
    useDeleteLinkedEnergyStarProperty,
    usePostLinkedEnergyStarProperty,
    usePutMainLinkedEnergyStarProperty,
  },
} as const;
export default linking;

// < 0 if a is before b
// > 0 if a is after b
const compareISO8601String = (a: string, b: string) =>
  new Date(a).getTime() - new Date(b).getTime();

// true if a is more recent than b or b is undefined
const isMoreRecent = (a?: string | null, b?: string | null) => {
  if (!b) {
    return true;
  }

  if (!a) {
    return false;
  }

  return compareISO8601String(a, b) > 0;
};

const getMainType = (
  backpackIsMain?: boolean,
  linkedPropertyIsMain?: boolean
): LinkedEnergyStarPropertyType | undefined => {
  if (backpackIsMain) {
    return "backpack";
  }
  if (linkedPropertyIsMain) {
    return "linked";
  }
};

const getLinkedEnergyStarProperties = async (siteId: number) => {
  const { data, error } = await FetchClients.site.GET(
    "/{site_id}/linked_es_properties",
    {
      params: {
        path: {
          site_id: siteId,
        },
      },
    }
  );

  if (data) return recursiveCamelCaseCipherTypesafe(data);
  throw new Error(error);
};

export type LinkedEnergyStarPropertyType = "backpack" | "linked";
export type EnergyStarProperty = CamelCasedProperties<
  ClientOutput["site"]["EnergyStarLinkedPropertyDetails"]
>;

const LINKED_ENERGY_STAR_PROPERTIES_KEY = "linked-es-properties";
function useGetLinkedEnergyStarProperties(siteId?: number) {
  return useReactQuery({
    queryKey: [LINKED_ENERGY_STAR_PROPERTIES_KEY, siteId],
    queryFn: () => getLinkedEnergyStarProperties(siteId!),
    enabled: siteId !== undefined,
    select: (energyStarProperties) => {
      const { backpackEnergyStarProperty, linkedEnergyStarProperty } =
        energyStarProperties.reduce(
          (acc, property) => {
            // There can be multiple backpack and linked properties, but we only care about the latest one
            if (
              property.isBpProperty &&
              isMoreRecent(
                property.linkedAt,
                acc.backpackEnergyStarProperty?.linkedAt
              )
            ) {
              acc.backpackEnergyStarProperty = property;
            }
            if (
              !property.isBpProperty &&
              isMoreRecent(
                property.linkedAt,
                acc.linkedEnergyStarProperty?.linkedAt
              )
            ) {
              acc.linkedEnergyStarProperty = property;
            }

            return acc;
          },
          {} as {
            backpackEnergyStarProperty?: EnergyStarProperty;
            linkedEnergyStarProperty?: EnergyStarProperty;
          }
        );

      return {
        backpackEnergyStarProperty,
        linkedEnergyStarProperty,

        mainEnergyStarProperty: [
          backpackEnergyStarProperty,
          linkedEnergyStarProperty,
        ].find((property) => property?.isMainProperty),
        historicalEnergyStarProperty: [
          backpackEnergyStarProperty,
          linkedEnergyStarProperty,
        ].find((property) => property && !property.isMainProperty),

        mainType: getMainType(
          backpackEnergyStarProperty?.isMainProperty,
          linkedEnergyStarProperty?.isMainProperty
        ),
      };
    },
  });
}

const deleteLinkedEnergyStarProperty = async (
  siteId: number,
  energyStarPropertyId: number
) => {
  const { data, error, response } = await FetchClients.site.DELETE(
    "/{site_id}/linked_es_properties/{linked_es_property_id}",
    {
      params: {
        path: {
          site_id: siteId,
          linked_es_property_id: energyStarPropertyId,
        },
      },
    }
  );
  if (response.status !== 200) {
    throw new Error(
      `Unable to remove linked energy star property: ${energyStarPropertyId}`
    );
  }
  if (data) return data;
  throw new Error(error);
};

function useDeleteLinkedEnergyStarProperty(siteId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (propertyId: number) =>
      deleteLinkedEnergyStarProperty(siteId, propertyId),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: [LINKED_ENERGY_STAR_PROPERTIES_KEY, siteId],
      }),
  });
}

type EnergyStarPropertyRequest = CamelCasedProperties<
  ClientOutput["site"]["EnergyStarPropertyLinkRequest"]
>;
const postLinkedEnergyStarProperty = async (
  siteId: number,
  energyStarProperty: EnergyStarPropertyRequest
) => {
  const { data, error, response } = await FetchClients.site.POST(
    "/{site_id}/linked_es_properties",
    {
      params: {
        path: {
          site_id: siteId,
        },
      },
      body: recursiveSnakeCaseCipherTypesafe(energyStarProperty),
    }
  );
  if (response.status !== 200) {
    throw new Error(
      `Unable to link energy star property: ${energyStarProperty.energyStarPropertyId}`
    );
  }
  if (data) return data;
  throw new Error(error);
};

function usePostLinkedEnergyStarProperty(siteId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (property: EnergyStarPropertyRequest) =>
      postLinkedEnergyStarProperty(siteId, property),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: [LINKED_ENERGY_STAR_PROPERTIES_KEY, siteId],
      }),
  });
}

const putMainLinkedEnergyStarProperty = async (
  siteId: number,
  propertyId: number
) => {
  const { data, error, response } = await FetchClients.site.PUT(
    "/{site_id}/main_linked_es_property",
    {
      params: {
        path: {
          site_id: siteId,
        },
      },
      body: {
        id: propertyId,
      },
    }
  );
  if (response.status !== 200) {
    throw new Error(
      `Unable to set primary energy star property: ${propertyId}`
    );
  }
  if (data) return data;
  throw new Error(error);
};

function usePutMainLinkedEnergyStarProperty(siteId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (propertyId: number) =>
      putMainLinkedEnergyStarProperty(siteId, propertyId),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: [LINKED_ENERGY_STAR_PROPERTIES_KEY, siteId],
      });
    },
  });
}
